import { useEffect, useRef, useState, useCallback } from "react";
import { api, API } from "@/lib/api";
import { Broadcast, ArrowsClockwise, MapPin, CheckCircle, XCircle, HourglassMedium, X, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import DispatchMap from "@/components/DispatchMap";
import { devWarn } from "@/lib/logger";

const STATUS_COLOR = {
  pending: "bg-emerald-100 text-emerald-800 border-emerald-300",
  accepted: "bg-blue-100 text-blue-800 border-blue-300",
  declined: "bg-slate-100 text-slate-700 border-slate-300",
  expired: "bg-amber-100 text-amber-800 border-amber-300",
  cancelled: "bg-slate-100 text-slate-700 border-slate-300",
};
const STATUS_ICON = {
  pending: <HourglassMedium size={12} weight="fill"/>,
  accepted: <CheckCircle size={12} weight="fill"/>,
  declined: <XCircle size={12}/>,
  expired: <Warning size={12} weight="fill"/>,
  cancelled: <X size={12}/>,
};
const JOB_STATE = {
  dispatching: "bg-emerald-100 text-emerald-800",
  filled: "bg-blue-100 text-blue-800",
  failed: "bg-red-100 text-red-800",
  closed: "bg-slate-100 text-slate-700",
};

function Pill({ children, className = "" }) {
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide rounded-full border ${className}`}>{children}</span>;
}

export default function AdminDispatch() {
  const [board, setBoard] = useState({ jobs: [], offers: [] });
  const [workers, setWorkers] = useState([]);
  const [heatmap, setHeatmap] = useState(null);
  const [connected, setConnected] = useState(false);
  const [flash, setFlash] = useState(null);
  const esRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [b, w, h] = await Promise.all([
        api.get("/admin/dispatch"),
        api.get("/admin/dispatch/workers/live"),
        api.get("/admin/dispatch/heatmap?res=7"),
      ]);
      setBoard(b.data || { jobs: [], offers: [] });
      setWorkers(w.data || []);
      setHeatmap(h.data || null);
    } catch (err) { devWarn("dispatch board load", err); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  useEffect(() => {
    let closed = false;
    const es = new EventSource(`${API}/admin/dispatch/stream`, { withCredentials: true });
    esRef.current = es;
    es.addEventListener("hello", () => setConnected(true));
    const bump = (label) => { setFlash(label); setTimeout(() => setFlash(null), 1200); load(); };
    ["offer_new_admin", "offer_accepted", "offer_declined", "offer_expired", "offer_cancelled",
      "job_live", "job_filled", "job_failed", "job_stopped"].forEach((k) =>
      es.addEventListener(k, () => bump(k.replaceAll("_", " ")))
    );
    es.onerror = () => { if (!closed) setConnected(false); };
    return () => { closed = true; es.close(); };
  }, [load]);

  const activeJobs = board.jobs || [];
  const offersByJob = (jobId) => (board.offers || []).filter((o) => o.job_id === jobId);

  return (
    <div className="space-y-5" data-testid="admin-dispatch-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 flex items-center gap-2">
            <Broadcast weight="fill" className="text-emerald-600" size={30}/> Live dispatch
          </h1>
          <div className="text-sm text-slate-600 mt-1 flex items-center gap-2">
            {connected
              ? <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"/>Live stream connected</span>
              : <span className="text-amber-700">Reconnecting…</span>}
            {flash && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 animate-pulse">event: {flash}</span>}
          </div>
        </div>
        <Button variant="outline" onClick={load} data-testid="dispatch-refresh"><ArrowsClockwise size={16} className="mr-1.5"/> Refresh</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <DispatchMap jobs={board.jobs || []} workers={workers} heatmap={heatmap} height={460} />
        </div>
        <div className="card-tactile p-4 space-y-2 text-sm">
          <div className="label-caps text-slate-500 mb-1">Coverage snapshot</div>
          <div className="flex items-center justify-between"><span>Live jobs</span><b>{(board.jobs || []).length}</b></div>
          <div className="flex items-center justify-between"><span>Workers on map</span><b>{workers.length}</b></div>
          <div className="flex items-center justify-between"><span>Live GPS</span><b>{workers.filter(w=>w.source==="live").length}</b></div>
          <div className="flex items-center justify-between"><span>Home fallback</span><b>{workers.filter(w=>w.source==="home").length}</b></div>
          {heatmap?.cells?.length > 0 && (
            <div className="flex items-center justify-between"><span>Supply hexes</span><b>{heatmap.cells.length} · peak {heatmap.max}</b></div>
          )}
          <div className="pt-2 mt-2 border-t border-slate-200 space-y-1.5 text-xs text-slate-600">
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-emerald-500"></span>Live GPS worker</div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-slate-400"></span>Home fallback worker</div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full ring-2 ring-emerald-500"></span>Dispatching job (radius = coverage)</div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full ring-2 ring-blue-500"></span>Filled job</div>
            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full ring-2 ring-red-500"></span>Failed dispatch</div>
            <div className="flex items-center gap-2 mt-1 pt-1 border-t border-slate-100"><span className="w-3 h-3" style={{background: "linear-gradient(90deg, hsl(140,70%,45%), hsl(0,70%,45%))"}}></span>Supply density hex (green = sparse, red = dense)</div>
          </div>
        </div>
      </div>

      {activeJobs.length === 0 && (
        <div className="card-tactile p-10 text-center text-slate-500">
          <Broadcast size={40} className="mx-auto mb-2 text-slate-300"/>
          No live jobs. A company must click <b>Go Live</b> on a published job.
        </div>
      )}

      <div className="space-y-4">
        {activeJobs.map((j) => {
          const offers = offersByJob(j.id).slice(0, 12);
          return (
            <div key={j.id} data-testid={`dispatch-job-${j.id}`} className="card-tactile p-5">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="label-caps text-blue-600 mb-0.5">{j.category}</div>
                  <div className="font-display text-xl font-bold text-slate-900">{j.title}</div>
                  <div className="text-sm text-slate-700 mt-1 flex flex-wrap gap-3">
                    <span className="flex items-center gap-1"><MapPin size={14}/>{j.location} · {j.postcode}</span>
                    <span>{j.date} · {j.start_time}–{j.end_time}</span>
                    <span className="font-bold">£{j.pay_rate}/hr</span>
                    <span>{j.staff_booked || 0}/{j.staff_required} booked</span>
                    <span>Radius {j.max_radius_km ?? 25} km</span>
                  </div>
                </div>
                <Pill className={JOB_STATE[j.dispatch_status] || "bg-slate-100 text-slate-700"}>{j.dispatch_status}</Pill>
              </div>
              {offers.length === 0 ? (
                <div className="mt-3 text-sm text-slate-500">No offers yet.</div>
              ) : (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {offers.map((o) => (
                    <div key={o.id} data-testid={`dispatch-offer-${o.id}`} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-sm font-semibold text-slate-900">
                          {o.worker?.first_name} {o.worker?.last_name}
                        </div>
                        <Pill className={STATUS_COLOR[o.status] || "bg-slate-100"}>{STATUS_ICON[o.status]}{o.status}</Pill>
                      </div>
                      <div className="text-xs text-slate-600 flex flex-wrap gap-3">
                        <span>Attempt #{o.attempt_num}</span>
                        <span>{o.distance_km} km</span>
                        <span className="font-bold text-slate-800">Score {(o.match_score * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
